import i18n from '@/utils/i18n';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AttachmentListControls } from './attachment-list-controls';
import { AttachmentListEML } from './attachment-list-eml';
import { AttachmentListHTML } from './attachment-list-html';
import { AttachmentListImage } from './attachment-list-image';
import { CursorValue, Image, ScrollView, StyleSheet, View } from 'react-native';
import { generateAttachmentList } from './utils';
import { OriginalInboundAudio } from '@/screens/order-inbounds/original-inbound-audio';
import { SAFE_AREA_PADDING } from '@/screens/order-inbounds/utils';
import type { AttachmentListProps } from './attachment-list-types';

export const AttachmentList: React.FC<AttachmentListProps> = ({
  attachments,
  imageHighlights,
  onAreaSelect,
  onEmlError,
  onHtmlHasNoContent,
  onHighlightElementSelect,
  orderUUID,
  segments,
  selectedAreaID,
  showHighlights,
  showTranslations,
}) => {
  const [aspectRatio, setAspectRatio] = useState<number | null>(null);
  const [imageIndex, setImageIndex] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [containerWidth, setContainerWidth] = useState(0);

  const isDraggingRef = useRef(false);
  const lastPositionRef = useRef({ x: 0, y: 0 });
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const locale = i18n.locale;

  const { emlAttachment, emlTranslations, filteredAttachments } = useMemo(
    () => generateAttachmentList(attachments),
    [attachments],
  );

  useEffect(() => {
    const attachment = filteredAttachments[imageIndex];
    if (!attachment) {
      return;
    }
    if (
      attachment.type === 'HTML' ||
      attachment.type === 'EML' ||
      attachment.type === 'AUDIO'
    ) {
      setAspectRatio(null);
      return;
    }
    Image.getSize(attachment.url, (width, height) => {
      setAspectRatio(width / height);
    });
  }, [imageIndex, filteredAttachments]);

  useEffect(() => {
    // Reset zoom level when image index changes
    setZoomLevel(1);
  }, [imageIndex]);

  useEffect(() => {
    const nextIndex = filteredAttachments.findIndex(
      (attachment) =>
        attachment.type === 'IMAGE_MAP' &&
        attachment.areas.some((area) => area.id === selectedAreaID),
    );

    setImageIndex((index) => {
      if (nextIndex !== -1 && index !== nextIndex) {
        return nextIndex;
      }

      return index;
    });
  }, [filteredAttachments, selectedAreaID]);

  const startDrag = (e: React.MouseEvent) => {
    if (scrollContainerRef.current) {
      isDraggingRef.current = true;
      lastPositionRef.current = { x: e.clientX, y: e.clientY };
      scrollContainerRef.current.style.cursor = 'grabbing';
    }
  };

  const stopDrag = () => {
    if (scrollContainerRef.current) {
      isDraggingRef.current = false;
      scrollContainerRef.current.style.cursor = 'grab';
    }
  };

  return (
    <View
      onLayout={(event) => {
        setContainerWidth(event.nativeEvent.layout.width);
      }}
      style={styles.base}
    >
      {emlAttachment ? (
        <AttachmentListEML
          emlAttachment={
            showTranslations && Boolean(emlTranslations[locale])
              ? emlTranslations[locale]
              : emlAttachment
          }
          imageHighlights={imageHighlights}
          key={
            showTranslations && Boolean(emlTranslations[locale])
              ? emlTranslations[locale].url
              : emlAttachment.url
          }
          onAreaSelect={onAreaSelect}
          onEmlError={onEmlError}
          onHighlightElementSelect={onHighlightElementSelect}
          onHtmlHasNoContent={onHtmlHasNoContent}
          selectedSearchInputUUID={selectedAreaID}
          showHighlights={showHighlights}
          width={containerWidth}
        />
      ) : null}

      {filteredAttachments.length > 0 ? (
        <>
          <div
            onMouseDown={startDrag}
            onMouseLeave={stopDrag}
            onMouseMove={(e) => {
              // Adjust scroll position opposite to mouse movement for panning effect
              if (isDraggingRef.current && scrollContainerRef.current) {
                const dx = e.clientX - lastPositionRef.current.x;
                const dy = e.clientY - lastPositionRef.current.y;

                scrollContainerRef.current.scrollLeft -= dx;
                scrollContainerRef.current.scrollTop -= dy;

                lastPositionRef.current = { x: e.clientX, y: e.clientY };
              }
            }}
            onMouseUp={stopDrag}
            ref={scrollContainerRef}
            style={StyleSheet.flatten([
              styles.scrollContainer,
              { aspectRatio: aspectRatio || undefined },
              styles.scrollContainerConstraints,
            ])}
          >
            {filteredAttachments.map((attachment, index) => {
              const isActive = index === imageIndex;

              if (attachment.type === 'AUDIO') {
                return (
                  <View
                    key={attachment.url}
                    style={[
                      isActive ? null : styles.imageContainerInactive,
                      styles.audioContainer,
                    ]}
                    testID={`audio-attachment-${index}-${isActive ? 'active' : 'inactive'}`}
                  >
                    <ScrollView
                      contentContainerStyle={styles.audioScrollContent}
                    >
                      <OriginalInboundAudio
                        attachmentIndex={attachment.attachmentIndex}
                        audioUrl={attachment.url}
                        onSelect={onHighlightElementSelect}
                        orderUUID={orderUUID}
                        segments={segments ?? []}
                        selectedInputUUID={selectedAreaID}
                        showTranslation={showTranslations}
                      />
                    </ScrollView>
                  </View>
                );
              }

              return (
                <View
                  key={attachment.url}
                  style={[
                    isActive ? null : styles.imageContainerInactive,
                    {
                      height: `${100 * zoomLevel}%`,
                      width: `${100 * zoomLevel}%`,
                    },
                  ]}
                  testID={`image-map-image-${index}-${isActive ? 'active' : 'inactive'}`}
                >
                  {attachment.type === 'HTML' ? (
                    <AttachmentListHTML
                      htmlURL={attachment.url}
                      maxHeight={isActive ? undefined : 100}
                      onHighlightElementSelect={onHighlightElementSelect}
                      selectedSearchInputUUID={selectedAreaID ?? ''}
                      showHighlights={showHighlights}
                      width={containerWidth}
                      zoomLevel={zoomLevel}
                    />
                  ) : (
                    <AttachmentListImage
                      imageMapAreas={
                        attachment.type === 'IMAGE_MAP' ? attachment.areas : []
                      }
                      imageURL={attachment.url}
                      onAreaSelect={onAreaSelect}
                      selectedAreaID={selectedAreaID}
                      width={containerWidth}
                      zoomLevel={zoomLevel}
                    />
                  )}
                </View>
              );
            })}
          </div>
          <AttachmentListControls
            hideZoomControls={filteredAttachments[imageIndex]?.type === 'AUDIO'}
            imageCount={filteredAttachments.length}
            imageIndex={imageIndex}
            setImageIndex={setImageIndex}
            setZoomLevel={(nextZoomLevel) => {
              setZoomLevel(nextZoomLevel);

              if (scrollContainerRef?.current) {
                const container = scrollContainerRef.current;
                const scaleFactor = nextZoomLevel / zoomLevel;

                // Find the centerpoint of the container
                const centerX =
                  container.scrollLeft + container.clientWidth / 2;
                const centerY =
                  container.scrollTop + container.clientHeight / 2;

                // Adjust scroll position to keep the same centerpoint in view
                requestAnimationFrame(() => {
                  if (scrollContainerRef?.current) {
                    scrollContainerRef.current.scrollLeft =
                      centerX * scaleFactor - container.clientWidth / 2;
                    scrollContainerRef.current.scrollTop =
                      centerY * scaleFactor - container.clientHeight / 2;
                  }
                });
              }
            }}
            zoomLevel={zoomLevel}
          />
        </>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  audioContainer: {
    height: '100%',
    width: '100%',
  },
  audioScrollContent: {
    padding: 16,
  },
  base: {
    paddingBottom: SAFE_AREA_PADDING,
    position: 'relative',
    userSelect: 'none',
  },
  imageContainerInactive: {
    display: 'none',
  },
  scrollContainer: {
    // Initial aspect ratio is 1:1
    aspectRatio: 1,
    cursor: 'grab' as CursorValue,
    display: 'flex',
    overflow: 'scroll',
    width: '100%',
  },
  scrollContainerConstraints: {
    maxHeight: 800,
    minHeight: 330,
  },
});
